import { SITE_URL } from './siteConfig';

/**
 * Centralized SEO metadata for public routes.
 * og/twitter title and description default to title/description in Seo.js.
 */
export const PAGE_SEO = {
  '/': {
    title: 'AI Interior, Exterior & Floor Plan Design | MyRoomsAI',
    description:
      'Design interiors, exteriors, gardens, and floorplans using AI. Transform rooms, generate 3D floorplans, and create realistic room renders with MyRoomsAI.',
    canonical: `${SITE_URL}/`,
    h1: 'Everything You Need for Home Design in One AI Platform',
  },
  '/ai-tools/ai-2d-floorplan-creator': {
    title: 'AI Floor Plan Creator & Layout Generator | MyRoomsAI',
    description:
      'Create professional 2D floorplans using AI-powered tools. Design rooms, walls, windows, and layouts directly in your browser.',
    canonical: `${SITE_URL}/ai-tools/ai-2d-floorplan-creator`,
    h1: 'AI Floor Plan Creator',
  },
  '/ai-tools/ai-2d-floorplan-designer': {
    title: 'AI 2D Floor Plan Designer Online | MyRoomsAI',
    description:
      'Draw and edit 2D floor plans online with MyRoomsAI. Create walls, rooms, doors, and windows using an easy AI-ready floor plan designer for home design and room planning.',
    canonical: `${SITE_URL}/ai-tools/ai-2d-floorplan-designer`,
    h1: 'AI 2D Floor Plan Designer',
  },
  '/ai-tools/ai-2d-floorplan-to-3d': {
    title: 'AI 2D Floor Plan to 3D Transformer | MyRoomsAI',
    description:
      'Turn 2D floor plans into 3D-style home previews with AI. Choose render style, furnishing, camera angle, interior style, and image quality.',
    canonical: `${SITE_URL}/ai-tools/ai-2d-floorplan-to-3d`,
    h1: 'AI 2D Floor Plan to 3D Transformer',
  },
  '/ai-tools/ai-2d-floorplan-to-room-render': {
    title: 'AI Floor Plan to Realistic Room Render | MyRoomsAI',
    description:
      'Transform 2D floorplans into realistic AI-generated room images. Convert floorplan layouts into interior design visualizations.',
    canonical: `${SITE_URL}/ai-tools/ai-2d-floorplan-to-room-render`,
    h1: 'AI Floor Plan to Realistic Room Generator',
  },
  '/ai-tools/ai-exterior-design': {
    title: 'AI Exterior Home Design & Renovation Tool | MyRoomsAI',
    description:
      'Transform house exteriors with AI exterior design. Generate modern facade ideas, home renovations, and realistic outdoor redesigns.',
    canonical: `${SITE_URL}/ai-tools/ai-exterior-design`,
    h1: 'AI Exterior Home Design',
  },
  '/ai-tools/ai-furniture-replacement': {
    title: 'AI Room Furniture Replacement Tool | MyRoomsAI',
    description:
      'Replace furniture in your room using AI. Redesign interiors with smart furniture replacement and realistic room transformations.',
    canonical: `${SITE_URL}/ai-tools/ai-furniture-replacement`,
    h1: 'AI Room Furniture Replacement',
  },
  '/ai-tools/ai-garden-design': {
    title: 'AI Garden Design & Landscaping Tool | MyRoomsAI',
    description:
      'Create beautiful outdoor spaces with AI garden design. Generate landscaping ideas, backyard concepts, and modern garden layouts.',
    canonical: `${SITE_URL}/ai-tools/ai-garden-design`,
    h1: 'AI Garden Design',
  },
  '/ai-tools/ai-room-decoration': {
    title: 'AI Room Decoration & Interior Design Tool | MyRoomsAI',
    description:
      'Decorate bedrooms, living rooms, kitchens, and offices using AI room decoration technology. Generate realistic room designs instantly.',
    canonical: `${SITE_URL}/ai-tools/ai-room-decoration`,
    h1: 'AI Room Decoration',
  },
  '/ai-tools/ai-3d-model-to-realistic-interior': {
    title: 'AI 3D Model to Realistic Interior Image | MyRoomsAI',
    description:
      'Convert 3D interior model screenshots from Blender, 3ds Max, Maya, SketchUp, or Three.js into realistic interior images. Preserve room structure, dimensions, camera angle, doors, windows, and layout while generating a photorealistic design.',
    canonical: `${SITE_URL}/ai-tools/ai-3d-model-to-realistic-interior`,
    h1: 'AI 3D Model to Realistic Interior Image',
  },
  '/pricing': {
    title: 'AI Home Design Pricing Plans | MyRoomsAI',
    description:
      'Explore MyRoomsAI pricing plans for AI room design, floorplan rendering, 3D visualization, and home renovation tools.',
    canonical: `${SITE_URL}/pricing`,
    h1: 'Pricing Plans',
  },
  '/real-transformations': {
    title: 'Real AI Home Design Transformations | MyRoomsAI',
    description:
      'Explore before and after AI home transformations including bedrooms, kitchens, living rooms, gardens, and exterior redesigns.',
    canonical: `${SITE_URL}/real-transformations`,
    h1: 'Real Room Transformations',
  },
  '/room-types/bedroom': {
    title: 'AI Bedroom Design Ideas & Room Decor | MyRoomsAI',
    description:
      'Discover AI bedroom design ideas, room decoration inspiration, modern bedroom layouts, and realistic bedroom transformations.',
    canonical: `${SITE_URL}/room-types/bedroom`,
    h1: 'Bedroom AI Design Ideas',
  },
  '/room-types/kitchen': {
    title: 'AI Kitchen Design Ideas & Remodels | MyRoomsAI',
    description:
      'Generate modern kitchen designs with AI. Explore layouts, cabinetry styles, and realistic kitchen renovation ideas.',
    canonical: `${SITE_URL}/room-types/kitchen`,
    h1: 'Kitchen AI Design Ideas',
  },
  '/room-types/living-room': {
    title: 'AI Living Room Design & Decor Ideas | MyRoomsAI',
    description:
      'Create stunning living room designs with AI. Explore modern interiors, furniture layouts, and room decoration concepts.',
    canonical: `${SITE_URL}/room-types/living-room`,
    h1: 'Living Room AI Design Ideas',
  },
  '/room-types/office': {
    title: 'AI Home Office Design Ideas | MyRoomsAI',
    description:
      'Design productive office spaces with AI. Generate home office layouts, workspace interiors, and modern office ideas.',
    canonical: `${SITE_URL}/room-types/office`,
    h1: 'Office AI Design Ideas',
  },
};

export function getPageSeo(pathname) {
  if (!pathname) return null;
  const cleanPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return PAGE_SEO[cleanPath] || null;
}
